// Typed pledge endpoint functions. Backed by GET /api/v1/pledges/*.
//
// Per CHG-1248 plan — consumed by @generated/screens/pledge-list.ts +
// pledge-detail.ts queryFn callbacks. Spec:
// specs/tier-04-interface/pledge-management.api.yaml.
//
// Payments uses native /pledges/{id}/payments. Schedule + Communications
// are NOT yet canonical at v4 — wrapped as /timeline?kind=schedule and
// /timeline?kind=communications bridges. Forward-compatible: when
// canonical endpoints ship, swap the internal URLs without page-code
// change.

import { apiClient } from "./client";
import {
  listPageParams,
  normalizeListResponse,
  type NormalizedList,
  type RawListEnvelope,
} from "./list-contract";
import type {
  Pledge,
  PledgePaymentSummary,
  PledgeScheduleEntry,
  PledgeDeliveryLogEntry,
  PagedEnvelope,
} from "./types";

export interface ListPledgesParams {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: "asc" | "desc";
  search?: string;
  status?: string;
  designation_id?: string;
  // DRIFT-1173 (CHG-3054) — opaque filter dict from the v2 ListPage
  // filters/filterChips slots (pledge.entity-hint.yaml pages.list_v2).
  // Keys match the entity-hint filter[].field / filter_chip[].field and
  // are mapped to the live PLG-R02 router params by mapPledgeFilters.
  filters?: Record<string, string | number | boolean | undefined>;
}

// DRIFT-1173 (CHG-3054) — pledge list-filter → live-router param mapping.
//
// PLG-R02 (CHG-3036) added these admin /pledges query params, all OPTIONAL
// with prior-behavior defaults (src/api/app/routers/pledges.py:list_pledges):
//   * source         (PledgeSourceChannel: dashboard/kiosk/widget/public/
//                     session/import — comma-list supported)
//   * designation_id (exact attribution filter)
//   * campaign_id    (exact attribution filter)
//   * overdue        (bool — past due_date & still committed/in_progress)
// The entity-hint filters/filter_chips emit these field keys; map them
// onto the router params verbatim. An inactive chip omits its key, so the
// router keeps its unfiltered default. The boolean `overdue` chip is
// forwarded only when active (omitting it = the router's `overdue=False`
// default anyway).
const PLEDGE_FILTER_KEYS = ["source", "designation_id", "campaign_id"] as const;

function mapPledgeFilters(
  filters: Record<string, string | number | boolean | undefined> | undefined,
): Record<string, string> {
  const out: Record<string, string> = {};
  if (!filters) return out;
  for (const key of PLEDGE_FILTER_KEYS) {
    const v = filters[key];
    if (v !== undefined && v !== null && v !== "") out[key] = String(v);
  }
  if (filters.overdue === true || filters.overdue === "true") {
    out.overdue = "true";
  }
  return out;
}

export async function listPledges(
  params: ListPledgesParams = {},
): Promise<NormalizedList<Pledge>> {
  // CHG-2916 / ITER-905655 — the /pledges router
  // (src/api/app/routers/pledges.py:list_pledges) returns the
  // `{ data, pagination }` PledgePagedEnvelope and reads 1-based
  // `page` / `per_page` / `search` / `status` (single or comma-list).
  // It has NO `sort` query param. DRIFT-1173 (CHG-3054) — PLG-R02 added
  // source / designation_id / campaign_id / overdue, threaded through
  // mapPledgeFilters from the v2 ListPage filters/filterChips slots.
  const r = await apiClient().get<RawListEnvelope<Pledge>>("/pledges", {
    params: {
      ...listPageParams(params),
      status: params.status,
      ...mapPledgeFilters(params.filters),
    },
  });
  return normalizeListResponse<Pledge>(r.data);
}

export async function getPledge(id: string): Promise<Pledge> {
  const r = await apiClient().get<Pledge>(`/pledges/${id}`);
  return r.data;
}

export async function getPledgePayments(
  id: string,
): Promise<PagedEnvelope<PledgePaymentSummary>> {
  const r = await apiClient().get<PagedEnvelope<PledgePaymentSummary>>(
    `/pledges/${id}/payments`,
  );
  return r.data;
}

// Schedule bridge — canonical /schedule endpoint pending; uses
// /timeline?kind=schedule projection.
export async function getPledgeSchedule(
  id: string,
): Promise<PagedEnvelope<PledgeScheduleEntry>> {
  const r = await apiClient().get<PagedEnvelope<PledgeScheduleEntry>>(
    `/pledges/${id}/timeline`,
    { params: { kind: "schedule" } },
  );
  return r.data;
}

// Communications bridge — canonical /delivery-logs endpoint pending;
// uses /timeline?kind=communications projection.
export async function getPledgeDeliveryLogs(
  id: string,
): Promise<PagedEnvelope<PledgeDeliveryLogEntry>> {
  const r = await apiClient().get<PagedEnvelope<PledgeDeliveryLogEntry>>(
    `/pledges/${id}/timeline`,
    { params: { kind: "communications" } },
  );
  return r.data;
}

export const pledgesApi = {
  listPledges,
  getPledge,
  getPledgePayments,
  getPledgeSchedule,
  getPledgeDeliveryLogs,
};
